import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { clearLogoutSuccess } from '../store/slices/authSlice';
import {useNavigate} from "react-router-dom";

const BannerWrapper = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: linear-gradient(135deg, var(--primary-orange) 0%, var(--primary-blue) 100%);
  color: white;
  padding: 1rem 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(-100%)'};
  opacity: ${props => props.isVisible ? '1' : '0'};
  transition: all 0.4s ease-in-out;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.1);
    z-index: -1;
  }
`;

const BannerContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }
`;

const BannerIcon = styled.div`
  font-size: 1.5rem;
  color: #4ade80;
  animation: pulse 2s ease-in-out infinite;
  
  @keyframes pulse {
    0%, 100% { 
      transform: scale(1); 
    }
    50% { 
      transform: scale(1.1); 
    }
  }
`;

const BannerText = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-50%) rotate(90deg);
  }
  
  @media (max-width: 768px) {
    position: relative;
    top: auto;
    right: auto;
    transform: none;
    margin-top: 0.5rem;
    
    &:hover {
      transform: rotate(90deg);
    }
  }
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
  }
  
  &::before {
    width: 60px;
    height: 60px;
    top: -20px;
    right: 20%;
    animation: float 4s ease-in-out infinite;
  }
  
  &::after {
    width: 40px;
    height: 40px;
    bottom: -10px;
    left: 30%;
    animation: float 6s ease-in-out infinite reverse;
  }
  
  @keyframes float {
    0%, 100% { 
      transform: translateY(0px) rotate(0deg); 
    }
    50% { 
      transform: translateY(-10px) rotate(5deg); 
    }
  }
`;

const LogoutSuccessBanner: React.FC = () => {
  const dispatch = useAppDispatch();
  const { logoutSuccess } = useAppSelector((state) => state.auth);
    const navigate = useNavigate();

  useEffect(() => {
    if (logoutSuccess) {
        navigate('/')
        // Auto-dismiss after 1 seconds
      const timer = setTimeout(() => {
        dispatch(clearLogoutSuccess());
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [logoutSuccess, dispatch]);

  const handleClose = () => {
    dispatch(clearLogoutSuccess());
  };

  if (!logoutSuccess) {
    return null;
  }

  return (
    <BannerWrapper isVisible={logoutSuccess}>
      <FloatingElements />
      <BannerContent>
        <BannerIcon>✅</BannerIcon>
        <BannerText>You have been successfully logged out. Welcome back anytime!</BannerText>
        <CloseButton onClick={handleClose}>×</CloseButton>
      </BannerContent>
    </BannerWrapper>
  );
};

export default LogoutSuccessBanner;